import React, { useEffect } from 'react';
import { generateCSRFToken, getOrCreateDeviceId } from '../utils/security';

// Simple session security replacement
const createSecureSession = async (email, role, provider) => {
  const deviceId = getOrCreateDeviceId();
  return {
    email,
    role,
    provider,
    deviceId,
    verified: true,
    loginTime: Date.now(),
    expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days
    lastActivity: Date.now(),
    schemaVersion: 3, // must match CURRENT_SCHEMA_VERSION in AuthContext
  };
};

const AuthVerify = () => {
  const [status, setStatus] = React.useState('verifying');
  const hasVerified = React.useRef(false);

  useEffect(() => {
    // Guard: StrictMode mounts effects twice in dev — only run once ever.
    if (hasVerified.current) return;
    hasVerified.current = true;

    const verifyToken = async () => {
      // Fix: some email clients (Gmail, Outlook) HTML-encode & as &amp; in href attributes.
      // When the browser receives such a URL, window.location.search contains the literal
      // string '&amp;' instead of '&', causing URLSearchParams to fail to parse the
      // email parameter (it gets appended to the token value instead).
      // We decode &amp; -> & in the raw search string before parsing.
      const rawSearch = window.location.search.replace(/&amp;/g, '&');
      const urlParams = new URLSearchParams(rawSearch);
      const token = urlParams.get('token');
      const email = urlParams.get('email');

      if (!token || !email) {
        setStatus('expired');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email) || email.length > 254) {
        setStatus('expired');
        return;
      }
      
      const sanitizedEmail = email.trim().toLowerCase();
      // Decode token — handles URL encoding and &amp; injected by email clients
      let rawToken = token;
      try {
        rawToken = decodeURIComponent(token);
      } catch (_) {
        rawToken = token;
      }
      rawToken = rawToken
        .replace(/&amp;/g, '&')
        .replace(/[<>"']/g, '')
        .substring(0, 300);
      const sanitizedToken = rawToken;

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        // Generate a CSRF token for the request
        const csrfToken = generateCSRFToken();

        const response = await fetch('/api/auth?action=verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            'Origin': window.location.origin,
          },
          body: JSON.stringify({ token: sanitizedToken, email: sanitizedEmail }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        const result = await response.json();

        if (result.success && result.sessionToken) {
          // SECURITY: role must come from server only — never from client email comparison.
          // If server doesn't return a role, default to 'user' (never elevate client-side).
          const userRole = result.role || 'user';

          const user = {
            id: result.userId || `verified-${Date.now()}`,
            email: sanitizedEmail,
            email_confirmed_at: result.verifiedAt || new Date().toISOString(),
            user_metadata: { full_name: sanitizedEmail.split('@')[0], role: userRole },
            role: userRole,
          };

          // 1. Write EVERYTHING to localStorage first — before any events or redirects
          localStorage.setItem('dev_auth_user', JSON.stringify(user));

          const authSession = await createSecureSession(sanitizedEmail, userRole, result.provider || 'magic-link');
          authSession.tokenAge = result.tokenAge;
          authSession.sessionDuration = result.sessionDuration;
          authSession.user = user;
          // SECURITY: store ONLY the server-issued sessionToken.
          // result.sessionToken is a NEW credential generated after verification.
          // The magic-link URL token (sanitizedToken) is NEVER stored here.
          authSession.token = result.sessionToken;
          authSession.schemaVersion = 3; // bump when TOKEN_SECRET rotates
          localStorage.setItem('auth_session', JSON.stringify(authSession));

          // 2. Update global auth
          window.dispatchEvent(new CustomEvent('auth-state-change', {
            detail: { user, session: authSession }
          }));

          try {
            const bc = new BroadcastChannel('tv-auth-sync');
            bc.postMessage({ type: 'LOGIN', user, session: authSession });
            bc.close();
          } catch (_) {}

          // 3. Show success UI immediately
          setStatus('success');

          // 4. Redirect to the requested return URL if provided, otherwise home.
          // PWA-safe navigation: use location.href (not replace) so the
          // back-stack is preserved in standalone mode on iOS/Android.
          // On iPhone PWA, location.replace() can exit the standalone shell.
          const rawReturnUrl = urlParams.get('returnUrl');
          const safeReturnUrl = (rawReturnUrl && 
                                 rawReturnUrl.startsWith('/') && 
                                 !rawReturnUrl.startsWith('//') && 
                                 !rawReturnUrl.includes('\\') && 
                                 !rawReturnUrl.includes(':')) ? rawReturnUrl : '/';
          const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                        window.navigator.standalone === true;
          setTimeout(() => {
            if (isPWA) {
              // In standalone PWA: use href to stay inside the shell
              window.location.href = safeReturnUrl;
            } else {
              // In browser: replace so the verify page isn't in history
              window.location.replace(safeReturnUrl);
            }
          }, 800);
        } else {
          setStatus('expired');
        }
      } catch (error) {
        const msg = error.name === 'AbortError'
          ? 'Verification timed out. Please check your connection and try again.'
          : 'Verification failed. Please try again.';
        setStatus('expired');
      }
    };

    verifyToken();
  }, []);

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" role="status" aria-live="polite">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-800 font-semibold text-lg">Verified! Redirecting...</p>
        </div>
      </div>
    );
  }

  // ── Expired / used / invalid link screen ─────────────────────────────────────
  if (status === 'expired' || status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4" role="alert">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-8 max-w-sm w-full text-center">
          <div className="w-14 h-14 bg-[#FFF7ED] rounded-2xl flex items-center justify-center mx-auto mb-5">
            <svg className="w-7 h-7 text-[#FF6600]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-[#0F172A] mb-2">Sign-in link expired</h2>
          <p className="text-sm text-[#64748B] leading-relaxed mb-6">
            This link can only be used once and has expired.<br />Please request a new one from the home page.
          </p>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="w-full bg-[#0057B7] hover:bg-[#004494] text-white text-sm font-semibold py-3 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" role="status" aria-live="polite">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0057B7] mx-auto mb-4" />
        <p className="text-gray-600">Verifying your account...</p>
        <p className="text-gray-400 text-xs mt-2">This usually takes a few seconds</p>
      </div>
    </div>
  );
};

export default AuthVerify;